import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiShield, FiEye, FiDownload, FiTrash2, FiEdit, FiMail } = FiIcons;

const PrivacyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiShield} className="text-blue-600 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-900">Personvern og cookies</h2>
              </div>
              <button
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <SafeIcon icon={FiX} className="text-gray-500 text-xl" />
              </button>
            </div>
          </div>

          <div className="px-6 py-6 space-y-8">
            {/* Quick Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Kort oppsummering</h3>
              <p className="text-blue-800 leading-relaxed">
                Vi samler kun inn nødvendig informasjon for å levere våre tjenester og følger strenge 
                personvernregler. Du har full kontroll over dine data og kan når som helst be om innsyn, 
                retting eller sletting av dine personopplysninger.
              </p>
            </div>

            {/* Cookie Information */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Informasjonskapsler (Cookies)</h3>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Vi bruker informasjonskapsler for å forbedre din opplevelse på vår nettside. 
                  Cookies er små tekstfiler som lagres på din enhet når du besøker nettsiden vår.
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Nødvendige cookies</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Disse er påkrevd for at nettsiden skal fungere og kan ikke deaktiveres.
                    </p>
                    <ul className="text-xs text-gray-500 space-y-1">
                      <li>• Innloggingsstatus og sikkerhet</li>
                      <li>• Språk- og regioninnstillinger</li>
                      <li>• Grunnleggende nettsidefunksjonalitet</li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Valgfrie cookies</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Disse forbedrer opplevelsen og kan deaktiveres.
                    </p>
                    <ul className="text-xs text-gray-500 space-y-1">
                      <li>• Bruksanalyse og forbedringer</li>
                      <li>• Personalisering av innhold</li>
                      <li>• Markedsføring og annonser</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Data Collection */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Hvilke data samler vi inn?</h3>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 bg-blue-50 p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Bedriftsinformasjon</h4>
                  <p className="text-blue-800 text-sm">
                    Organisasjonsnummer, bedriftsnavn, kontaktinformasjon og faktureringsadresse 
                    for å levere våre tjenester og håndtere abonnement.
                  </p>
                </div>

                <div className="border-l-4 border-green-500 bg-green-50 p-4">
                  <h4 className="font-medium text-green-900 mb-2">Brukerkontoinformasjon</h4>
                  <p className="text-green-800 text-sm">
                    E-postadresse, navn og rolle for å administrere tilgang til plattformen 
                    og sende nødvendige varsler.
                  </p>
                </div>

                <div className="border-l-4 border-orange-500 bg-orange-50 p-4">
                  <h4 className="font-medium text-orange-900 mb-2">Varslingsdata</h4>
                  <p className="text-orange-800 text-sm">
                    Anonyme varsler og HMS-rapporter behandles konfidensielt og lagres sikkert 
                    i henhold til juridiske krav og oppfølgingsformål.
                  </p>
                </div>
              </div>
            </section>

            {/* GDPR Rights */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Dine rettigheter (GDPR)</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg">
                  <SafeIcon icon={FiEye} className="text-blue-600 text-xl flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Rett til innsyn</h4>
                    <p className="text-sm text-gray-600">
                      Du kan be om å få se hvilke personopplysninger vi har om deg.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg">
                  <SafeIcon icon={FiEdit} className="text-green-600 text-xl flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Rett til retting</h4>
                    <p className="text-sm text-gray-600">
                      Du kan be om å rette feil eller utdaterte opplysninger.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg">
                  <SafeIcon icon={FiTrash2} className="text-red-600 text-xl flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Rett til sletting</h4>
                    <p className="text-sm text-gray-600">
                      Du kan be om sletting av dine personopplysninger der det er lovlig.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg">
                  <SafeIcon icon={FiDownload} className="text-purple-600 text-xl flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Rett til dataportabilitet</h4>
                    <p className="text-sm text-gray-600">
                      Du kan be om å få dine data i et strukturert, maskinlesbart format.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Contact Information */}
            <section className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Kontakt oss om personvern</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiMail} className="text-blue-600" />
                  <div>
                    <span className="font-medium text-gray-900">E-post:</span>
                    <span className="text-gray-700 ml-2">personvern@hmsvarsling.no</span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  <strong>Behandlingsansvarlig:</strong> Employer Brand AS<br />
                  <strong>Org.nr:</strong> 925 182 672 MVA<br />
                  <strong>Adresse:</strong> Motzfeldts gate 21, 0561 Oslo
                </p>
                <p className="text-sm text-gray-600">
                  Vi svarer på henvendelser om personvern innen 30 dager.
                </p>
              </div>
            </section>
          </div>

          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-2xl">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Lukk
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PrivacyModal;